import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import { SCHEMES } from "./constants";
import { MesocycleFinisherPanel } from "./MesocycleFinisher";

export function ProgressionSchemeDialog({ programId, totalWeeks, sourceDay, sourceWeek, sourceSection, exerciseName, sessionType, onClose, onApplied }) {
  const [scheme, setScheme] = useState("linear");
  const [base1rm, setBase1rm] = useState(100);
  const [weeksFrom, setWeeksFrom] = useState(sourceWeek || 1);
  const [weeksTo, setWeeksTo] = useState(Math.max(sourceWeek || 1, Math.min(totalWeeks || 4, (sourceWeek || 1) + 3)));
  const [grid, setGrid] = useState([]);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [customGrid, setCustomGrid] = useState([]);
  const [appliedContext, setAppliedContext] = useState(null);
  const [showFinisher, setShowFinisher] = useState(false);

  const weeksCount = Math.max(1, weeksTo - weeksFrom + 1);

  const preview = async () => {
    setLoading(true);
    try {
      const body = { scheme, weeks: weeksCount, base_1rm: Number(base1rm), session_type: sessionType };
      if (scheme === "custom") body.custom_grid = customGrid.length ? customGrid : [{ week: 1, sets: 3, reps: 5, percentage_1rm: 75 }];
      const { data } = await api.post("/coach/programs/schemes/preview", body);
      setGrid(data.grid || []);
      if (scheme === "custom" && !customGrid.length) setCustomGrid(data.grid);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally { setLoading(false); }
  };

  React.useEffect(() => { preview(); }, [scheme, base1rm, weeksFrom, weeksTo]);

  const apply = async () => {
    if (!exerciseName?.trim()) return toast.error("This exercise has no name — set one first");
    setApplying(true);
    try {
      const body = {
        scheme, base_1rm: Number(base1rm),
        weeks_from: Number(weeksFrom), weeks_to: Number(weeksTo),
        source_day: sourceDay, source_section: sourceSection,
        exercise_name: exerciseName, session_type: sessionType,
      };
      if (scheme === "custom") body.custom_grid = customGrid;
      const { data } = await api.post(`/coach/programs/${programId}/apply-scheme`, body);
      toast.success(`${scheme.toUpperCase()} applied across ${data.weeks_updated} weeks`);
      setAppliedContext({
        source_day: sourceDay,
        source_section: sourceSection,
        exercise_name: exerciseName,
        session_type: sessionType,
        base_1rm: Number(base1rm),
      });
      onApplied();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed");
    } finally { setApplying(false); }
  };

  const updateCustomCell = (idx, key, val) => {
    const next = [...customGrid];
    next[idx] = { ...next[idx], [key]: key === "sets" || key === "percentage_1rm" ? Number(val) : val, week: (weeksFrom + idx) };
    setCustomGrid(next);
    setGrid(next);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-surface-DEFAULT border-white/20 text-white max-w-4xl" data-testid="scheme-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-white">PROGRESSION · {exerciseName?.toUpperCase() || "EXERCISE"}</DialogTitle>
          <DialogDescription className="text-xs text-white/70">
            Pick a scheme, set your base 1RM, and preview each week&rsquo;s prescription. Applying will upsert the entries across weeks {weeksFrom} — {weeksTo} on Day {sourceDay} · <span className="!text-yellow-400">{sourceSection.toUpperCase()}</span>.
          </DialogDescription>
        </DialogHeader>

        <div>
          <label className="label-tiny text-[11px] text-white/70 block mb-2">SCHEME</label>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-1.5" data-testid="scheme-picker">
            {SCHEMES.map((s) => (
              <button
                key={s.id}
                onClick={() => setScheme(s.id)}
                data-testid={`scheme-opt-${s.id}`}
                className={`text-[11px] label-tiny py-2.5 px-2 border transition-colors font-medium ${scheme === s.id ? "!bg-yellow-400 text-slate-900 !border-yellow-400 shadow-lg shadow-yellow-400/30" : "border-white/25 text-white/85 hover:!border-yellow-400 hover:!text-yellow-400 bg-surface-elevated/60"}`}
                title={s.hint}
              >
                {s.label.toUpperCase()}
              </button>
            ))}
          </div>
          <div className="text-[11px] text-white/60 mt-2 italic">{SCHEMES.find((s) => s.id === scheme)?.hint}</div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="label-tiny text-[11px] text-white/70 block mb-1.5">BASE 1RM (KG)</label>
            <Input type="number" min="1" value={base1rm} onChange={(e) => setBase1rm(e.target.value)} data-testid="scheme-base-1rm" className="bg-surface-elevated border-white/25 text-white text-sm h-9" />
          </div>
          <div>
            <label className="label-tiny text-[11px] text-white/70 block mb-1.5">WEEKS FROM</label>
            <Input type="number" min="1" value={weeksFrom} onChange={(e) => setWeeksFrom(e.target.value)} data-testid="scheme-weeks-from" className="bg-surface-elevated border-white/25 text-white text-sm h-9" />
          </div>
          <div>
            <label className="label-tiny text-[11px] text-white/70 block mb-1.5">WEEKS TO</label>
            <Input type="number" min="1" value={weeksTo} onChange={(e) => setWeeksTo(e.target.value)} data-testid="scheme-weeks-to" className="bg-surface-elevated border-white/25 text-white text-sm h-9" />
          </div>
        </div>

        <div>
          <label className="label-tiny text-[11px] text-white/70 block mb-1.5">WEEKLY PRESCRIPTION</label>
          <div className="border border-white/20 max-h-72 overflow-y-auto bg-surface-base/40" data-testid="scheme-preview-grid">
            <div className="grid grid-cols-6 text-[11px] label-tiny text-white/70 border-b-2 !border-yellow-400/50 sticky top-0 bg-surface-elevated font-bold">
              <div className="px-3 py-2">WK</div>
              <div className="px-3 py-2">SETS</div>
              <div className="px-3 py-2">REPS</div>
              <div className="px-3 py-2">%1RM</div>
              <div className="px-3 py-2">KG</div>
              <div className="px-3 py-2">NOTES</div>
            </div>
            {loading ? <div className="p-4 text-xs text-white/60">Computing…</div> : grid.length === 0 ? (
              <div className="p-4 text-xs text-white/60">No preview yet.</div>
            ) : grid.map((r, i) => (
              <div key={i} className="grid grid-cols-6 text-sm border-b border-white/10 tabular-nums items-center hover:bg-white/[0.03]" data-testid={`scheme-row-${r.week}`}>
                {scheme === "custom" ? (
                  <>
                    <div className="px-3 py-2 font-heading !text-yellow-400 text-base">W{r.week}</div>
                    <div className="px-2 py-1.5"><Input type="number" value={r.sets} onChange={(e) => updateCustomCell(i, "sets", e.target.value)} className="h-7 text-sm bg-surface-elevated border-white/25 text-white" data-testid={`scheme-row-${r.week}-sets`} /></div>
                    <div className="px-2 py-1.5"><Input value={r.reps} onChange={(e) => updateCustomCell(i, "reps", e.target.value)} className="h-7 text-sm bg-surface-elevated border-white/25 text-white" data-testid={`scheme-row-${r.week}-reps`} /></div>
                    <div className="px-2 py-1.5"><Input type="number" value={r.percentage_1rm} onChange={(e) => updateCustomCell(i, "percentage_1rm", e.target.value)} className="h-7 text-sm bg-surface-elevated border-white/25 text-white" data-testid={`scheme-row-${r.week}-pct`} /></div>
                    <div className="px-3 py-2 !text-yellow-400 font-heading">{Math.round(Number(base1rm) * Number(r.percentage_1rm) / 100 / 2.5) * 2.5}</div>
                    <div className="px-2 py-1.5"><Input value={r.notes || ""} onChange={(e) => updateCustomCell(i, "notes", e.target.value)} className="h-7 text-sm bg-surface-elevated border-white/25 text-white" data-testid={`scheme-row-${r.week}-notes`} /></div>
                  </>
                ) : (
                  <>
                    <div className="px-3 py-2 font-heading !text-yellow-400 text-base">W{r.week}</div>
                    <div className="px-3 py-2 text-white">{r.sets}</div>
                    <div className="px-3 py-2 text-white">{r.reps}</div>
                    <div className="px-3 py-2 text-white/90">{r.percentage_1rm}%</div>
                    <div className="px-3 py-2 !text-yellow-400 font-heading">{r.weight_kg}</div>
                    <div className="px-3 py-2 text-white/70 text-xs">{r.notes}</div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={apply}
          disabled={applying || grid.length === 0}
          className="!bg-yellow-400 hover:bg-yellow-300 text-slate-900 font-bold transition-colors w-full py-3.5 text-sm label-tiny tracking-widest disabled:opacity-50 font-bold"
          data-testid="scheme-apply-button"
        >
          {applying ? "APPLYING..." : `APPLY ${scheme.toUpperCase()} — ${weeksCount} WEEK${weeksCount === 1 ? "" : "S"}`}
        </button>

        {appliedContext && !showFinisher && (
          <div
            className="mt-3 border !border-yellow-400/60 !bg-yellow-400/10 p-3 flex items-center justify-between gap-3"
            data-testid="finisher-cta-banner"
          >
            <div className="text-xs text-white/90">
              <div className="label-tiny !text-yellow-400 mb-0.5">// FINISH MESOCYCLE</div>
              <div>Cap this block with a one-tap <span className="!text-yellow-400 font-bold">Deload</span> or <span className="!text-yellow-400 font-bold">Peak</span> week.</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => setAppliedContext(null)}
                className="text-[11px] label-tiny tracking-widest text-white/60 hover:text-white px-2 py-1.5"
                data-testid="finisher-cta-dismiss"
              >
                DISMISS
              </button>
              <button
                onClick={() => setShowFinisher(true)}
                className="!bg-yellow-400 hover:bg-yellow-300 text-slate-900 font-bold text-[11px] label-tiny tracking-widest px-3 py-1.5"
                data-testid="finisher-cta-open"
              >
                FINISH MESOCYCLE →
              </button>
            </div>
          </div>
        )}

        {appliedContext && showFinisher && (
          <MesocycleFinisherPanel
            programId={programId}
            sourceDay={appliedContext.source_day}
            sourceSection={appliedContext.source_section}
            exerciseName={appliedContext.exercise_name}
            sessionType={appliedContext.session_type}
            initialBase1rm={appliedContext.base_1rm}
            onCancel={() => setShowFinisher(false)}
            onApplied={() => { setShowFinisher(false); setAppliedContext(null); onApplied(); onClose(); }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
